import React, { createContext, useContext, memo, useState } from 'react';
import useDebounce from 'react-use/lib/useDebounce';
import { useStoreState, BezierEdge, StraightEdge, EdgeText, getMarkerEnd } from 'react-flow-renderer';
import { Grid, AStarFinder, Util, DiagonalMovement } from 'pathfinding';
import warning from 'tiny-warning';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var getNextPointFromPosition = function getNextPointFromPosition(point, position) {
  switch (position) {
    case 'top':
      return {
        x: point.x,
        y: point.y - 1
      };

    case 'bottom':
      return {
        x: point.x,
        y: point.y + 1
      };

    case 'left':
      return {
        x: point.x - 1,
        y: point.y
      };

    case 'right':
      return {
        x: point.x + 1,
        y: point.y
      };
  }
};
/**
 * Guarantee that the path is walkable, even if the point is inside a non
 * walkable area, by adding a walkable path in the direction of the point's
 * Position.
 */


var guaranteeWalkablePath = function guaranteeWalkablePath(grid, point, position) {
  var node = grid.getNodeAt(point.x, point.y);

  while (!node.walkable) {
    grid.setWalkableAt(node.x, node.y, true);
    var next = getNextPointFromPosition(node, position);
    node = grid.getNodeAt(next.x, next.y);
  }
};
/**
 * Each bounding box is a collection of X/Y points in a graph, and we
 * need to convert them to "occupied" cells in a 2D grid representation.
 *
 * The top most position of the grid (grid[0][0]) needs to be equivalent
 * to the top most point in the graph (the graph.topLeft point).
 *
 * Since the top most point can have X/Y values different than zero,
 * and each cell in a grid represents a 10x10 pixel area in the grid (or a
 * gridRatio area), there's need to be a conversion between a point in a graph
 * to a point in the grid.
 *
 * We do this conversion by dividing a graph point X/Y values by the grid ratio,
 * and "shifting" their values up or down, depending on the values of the top
 * most point in the graph. The top most point in the graph will have the
 * smallest values for X and Y.
 *
 * We avoid setting nodes in the border of the grid (x=0 or y=0), so there's
 * always a "walkable" area around the grid.
 */


var graphToGridPoint = function graphToGridPoint(graphPoint, smallestX, smallestY, gridRatio) {
  var x = graphPoint.x / gridRatio;
  var y = graphPoint.y / gridRatio;
  var referenceX = smallestX / gridRatio;
  var referenceY = smallestY / gridRatio;

  if (referenceX < 1) {
    while (referenceX !== 1) {
      referenceX++;
      x++;
    }
  } else if (referenceX > 1) {
    while (referenceX !== 1) {
      referenceX--;
      x--;
    }
  }

  if (referenceY < 1) {
    while (referenceY !== 1) {
      referenceY++;
      y++;
    }
  } else if (referenceY > 1) {
    while (referenceY !== 1) {
      referenceY--;
      y--;
    }
  }

  return {
    x: x,
    y: y
  };
};
/**
 * Converts a grid point back to a graph point, using the reverse logic of
 * graphToGridPoint.
 */


var gridToGraphPoint = function gridToGraphPoint(gridPoint, smallestX, smallestY, gridRatio) {
  var x = gridPoint.x * gridRatio;
  var y = gridPoint.y * gridRatio;
  var referenceX = smallestX;
  var referenceY = smallestY;

  if (referenceX < gridRatio) {
    while (referenceX !== gridRatio) {
      referenceX = referenceX + gridRatio;
      x = x - gridRatio;
    }
  } else if (referenceX > gridRatio) {
    while (referenceX !== gridRatio) {
      referenceX = referenceX - gridRatio;
      x = x + gridRatio;
    }
  }

  if (referenceY < gridRatio) {
    while (referenceY !== gridRatio) {
      referenceY = referenceY + gridRatio;
      y = y - gridRatio;
    }
  } else if (referenceY > gridRatio) {
    while (referenceY !== gridRatio) {
      referenceY = referenceY - gridRatio;
      y = y + gridRatio;
    }
  }

  return {
    x: x,
    y: y
  };
};

var round = function round(x, multiple) {
  if (multiple === void 0) {
    multiple = 10;
  }

  return Math.round(x / multiple) * multiple;
};

var roundDown = function roundDown(x, multiple) {
  if (multiple === void 0) {
    multiple = 10;
  }

  return Math.floor(x / multiple) * multiple;
};

var roundUp = function roundUp(x, multiple) {
  if (multiple === void 0) {
    multiple = 10;
  }

  return Math.ceil(x / multiple) * multiple;
};

var toInteger = function toInteger(value, min) {
  if (min === void 0) {
    min = 0;
  }

  var result = Math.max(Math.round(value), min);
  result = Number.isInteger(result) ? result : min;
  result = result >= min ? result : min;
  return result;
};

var createGrid = function createGrid(graph, nodes, source, target, gridRatio) {
  if (gridRatio === void 0) {
    gridRatio = 2;
  }

  var xMin = graph.xMin,
      yMin = graph.yMin,
      width = graph.width,
      height = graph.height; // Create a grid representation of the graph box, where each cell is
  // equivalent to 10x10 pixels (or the grid ratio) on the graph. We'll use
  // this simplified grid to do pathfinding.

  var mapColumns = roundUp(width, gridRatio) / gridRatio + 1;
  var mapRows = roundUp(height, gridRatio) / gridRatio + 1;
  var grid = new Grid(mapColumns, mapRows); // Update the grid representation with the space the nodes take up

  nodes.forEach(function (node) {
    var nodeStart = graphToGridPoint(node.topLeft, xMin, yMin, gridRatio);
    var nodeEnd = graphToGridPoint(node.bottomRight, xMin, yMin, gridRatio);

    for (var x = nodeStart.x; x < nodeEnd.x; x++) {
      for (var y = nodeStart.y; y < nodeEnd.y; y++) {
        grid.setWalkableAt(x, y, false);
      }
    }
  }); // Convert the starting and ending graph points to grid points

  var startGrid = graphToGridPoint({
    x: round(source.x, gridRatio),
    y: round(source.y, gridRatio)
  }, xMin, yMin, gridRatio);
  var endGrid = graphToGridPoint({
    x: round(target.x, gridRatio),
    y: round(target.y, gridRatio)
  }, xMin, yMin, gridRatio); // Guarantee a walkable path between the start and end points, even if the
  // source or target where covered by another node or by padding

  var startingNode = grid.getNodeAt(startGrid.x, startGrid.y);
  guaranteeWalkablePath(grid, startingNode, source.position);
  var endingNode = grid.getNodeAt(endGrid.x, endGrid.y);
  guaranteeWalkablePath(grid, endingNode, target.position); // Use the next closest points as the start and end points, so
  // pathfinding does not start too close to the nodes

  var start = getNextPointFromPosition(startingNode, source.position);
  var end = getNextPointFromPosition(endingNode, target.position);
  return {
    grid: grid,
    start: start,
    end: end
  };
};
/**
 * Draws a SVG path from a list of points, using straight lines.
 */


var drawStraightLinePath = function drawStraightLinePath(source, target, path) {
  var svgPathString = "M " + source.x + ", " + source.y + " ";
  path.forEach(function (point) {
    var x = point[0],
        y = point[1];
    svgPathString += "L " + x + ", " + y + " ";
  });
  svgPathString += "L " + target.x + ", " + target.y + " ";
  return svgPathString;
};
/**
 * Draws a SVG path from a list of points, using rounded lines.
 */


var drawSmoothLinePath = function drawSmoothLinePath(source, target, path) {
  var points = [[source.x, source.y]].concat(path, [[target.x, target.y]]);
  return quadraticBezierCurve(points);
};

var quadraticBezierCurve = function quadraticBezierCurve(points) {
  var X = 0;
  var Y = 1;
  var point = points[0];
  var first = points[0];
  var svgPath = "M" + first[X] + "," + first[Y] + "M";

  for (var i = 0; i < points.length; i++) {
    var next = points[i];
    var midPoint = getMidPoint(point[X], point[Y], next[X], next[Y]);
    svgPath += " " + midPoint[X] + "," + midPoint[Y];
    svgPath += "Q" + next[X] + "," + next[Y];
    point = next;
  }

  var last = points[points.length - 1];
  svgPath += " " + last[0] + "," + last[1];
  return svgPath;
};

var getMidPoint = function getMidPoint(Ax, Ay, Bx, By) {
  var Zx = (Ax - Bx) / 2 + Bx;
  var Zy = (Ay - By) / 2 + By;
  return [Zx, Zy];
};

var withDiagonalMovement = {
  allowDiagonal: true,
  dontCrossCorners: true,
  diagonalMovement: DiagonalMovement.Always
};
var withStraightMovement = {
  allowDiagonal: false
};

var generatePath = function generatePath(grid, start, end, lessCorners) {
  var finderOptions = lessCorners ? withStraightMovement : withDiagonalMovement;
  var finder = new AStarFinder(finderOptions);
  var fullPath = [];
  var smoothedPath = [];

  try {
    fullPath = finder.findPath(start.x, start.y, end.x, end.y, grid);
    smoothedPath = Util.smoothenPath(grid, fullPath);
  } catch (_unused) {// No path was found. This can happen if the end point is "surrounded"
    // by other nodes, or if the starting and ending nodes are on top of
    // each other.
  }

  return {
    fullPath: fullPath,
    smoothedPath: smoothedPath
  };
};
/**
 * Get the bounding box of all nodes and the graph itself, as X/Y coordinates
 * of all corner points.
 *
 * @param storeNodes The node list
 * @param nodePadding Optional padding to add to the node's and graph bounding boxes
 * @param roundTo Everything will be rounded to this nearest integer
 * @returns Graph and nodes bounding boxes.
 */


var getBoundingBoxes = function getBoundingBoxes(storeNodes, nodePadding, roundTo) {
  if (nodePadding === void 0) {
    nodePadding = 2;
  }

  if (roundTo === void 0) {
    roundTo = 2;
  }

  var xMax = Number.MIN_SAFE_INTEGER;
  var yMax = Number.MIN_SAFE_INTEGER;
  var xMin = Number.MAX_SAFE_INTEGER;
  var yMin = Number.MAX_SAFE_INTEGER;
  var nodes = storeNodes.map(function (node) {
    var _rf$position, _rf$position2;

    var rf = node == null ? void 0 : node.__rf;
    var width = Math.max((rf == null ? void 0 : rf.width) || 0, 1);
    var height = Math.max((rf == null ? void 0 : rf.height) || 0, 1);
    var position = {
      x: (rf == null ? void 0 : (_rf$position = rf.position) == null ? void 0 : _rf$position.x) || 0,
      y: (rf == null ? void 0 : (_rf$position2 = rf.position) == null ? void 0 : _rf$position2.y) || 0
    };
    var topLeft = {
      x: position.x - nodePadding,
      y: position.y - nodePadding
    };
    var bottomLeft = {
      x: position.x - nodePadding,
      y: position.y + height + nodePadding
    };
    var topRight = {
      x: position.x + width + nodePadding,
      y: position.y - nodePadding
    };
    var bottomRight = {
      x: position.x + width + nodePadding,
      y: position.y + height + nodePadding
    };

    if (roundTo > 0) {
      topLeft.x = roundDown(topLeft.x, roundTo);
      topLeft.y = roundDown(topLeft.y, roundTo);
      bottomLeft.x = roundDown(bottomLeft.x, roundTo);
      bottomLeft.y = roundUp(bottomLeft.y, roundTo);
      topRight.x = roundUp(topRight.x, roundTo);
      topRight.y = roundDown(topRight.y, roundTo);
      bottomRight.x = roundUp(bottomRight.x, roundTo);
      bottomRight.y = roundUp(bottomRight.y, roundTo);
    }

    if (topLeft.y < yMin) yMin = topLeft.y;
    if (topLeft.x < xMin) xMin = topLeft.x;
    if (bottomRight.y > yMax) yMax = bottomRight.y;
    if (bottomRight.x > xMax) xMax = bottomRight.x;
    return {
      id: node.id,
      width: width,
      height: height,
      topLeft: topLeft,
      bottomLeft: bottomLeft,
      topRight: topRight,
      bottomRight: bottomRight
    };
  });
  var graphPadding = nodePadding * 2;
  xMax = roundUp(xMax + graphPadding, roundTo);
  yMax = roundUp(yMax + graphPadding, roundTo);
  xMin = roundDown(xMin - graphPadding, roundTo);
  yMin = roundDown(yMin - graphPadding, roundTo);
  var topLeft = {
    x: xMin,
    y: yMin
  };
  var bottomLeft = {
    x: xMin,
    y: yMax
  };
  var topRight = {
    x: xMax,
    y: yMin
  };
  var bottomRight = {
    x: xMax,
    y: yMax
  };
  var width = Math.abs(topLeft.x - topRight.x);
  var height = Math.abs(topLeft.y - bottomLeft.y);
  var graph = {
    topLeft: topLeft,
    bottomLeft: bottomLeft,
    topRight: topRight,
    bottomRight: bottomRight,
    width: width,
    height: height,
    xMax: xMax,
    yMax: yMax,
    xMin: xMin,
    yMin: yMin
  };
  return {
    nodes: nodes,
    graph: graph
  };
};

var defaultOptions = {
  debounceTime: 200,
  nodePadding: 10,
  gridRatio: 10,
  lineType: 'curve',
  lessCorners: false
};
var SmartEdgeContext = /*#__PURE__*/createContext(defaultOptions);

var SmartEdgeProvider = function SmartEdgeProvider(_ref) {
  var children = _ref.children,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? defaultOptions : _ref$options;
  var _options$debounceTime = options.debounceTime,
      debounceTime = _options$debounceTime === void 0 ? 200 : _options$debounceTime,
      _options$nodePadding = options.nodePadding,
      nodePadding = _options$nodePadding === void 0 ? 10 : _options$nodePadding,
      _options$gridRatio = options.gridRatio,
      gridRatio = _options$gridRatio === void 0 ? 10 : _options$gridRatio,
      _options$lineType = options.lineType,
      lineType = _options$lineType === void 0 ? 'curve' : _options$lineType,
      _options$lessCorners = options.lessCorners,
      lessCorners = _options$lessCorners === void 0 ? false : _options$lessCorners; // Guarantee that all values are positive integers

  gridRatio = toInteger(gridRatio, 2);
  nodePadding = toInteger(nodePadding, 2);
  debounceTime = toInteger(debounceTime); // Guarantee correct line type

  if (lineType !== 'curve' && lineType !== 'straight') {
    lineType = 'curve';
  }

  process.env.NODE_ENV !== "production" ? warning(debounceTime >= 30, 'A small debounce time on SmartEdge can cause performance issues on large graphs.') : void 0;
  process.env.NODE_ENV !== "production" ? warning(gridRatio >= 10, 'A small grid ratio on SmartEdge can cause performance issues on large graphs.') : void 0;
  return React.createElement(SmartEdgeContext.Provider, {
    value: {
      debounceTime: debounceTime,
      nodePadding: nodePadding,
      gridRatio: gridRatio,
      lineType: lineType,
      lessCorners: lessCorners
    }
  }, children);
};

var useSmartEdge = function useSmartEdge() {
  var context = useContext(SmartEdgeContext);

  if (context === undefined) {
    throw new Error('useSmartEdge must be used within a SmartEdgeProvider');
  }

  if (context.debounceTime === undefined || context.gridRatio === undefined || context.nodePadding === undefined || context.lineType === undefined) {
    throw new Error('Missing options on SmartEdgeProvider');
  }

  return context;
};

var PathFindingEdge = /*#__PURE__*/memo(function (props) {
  var sourceX = props.sourceX,
      sourceY = props.sourceY,
      sourcePosition = props.sourcePosition,
      targetX = props.targetX,
      targetY = props.targetY,
      targetPosition = props.targetPosition,
      arrowHeadType = props.arrowHeadType,
      markerEndId = props.markerEndId,
      style = props.style,
      storeNodes = props.storeNodes,
      label = props.label,
      labelStyle = props.labelStyle,
      labelShowBg = props.labelShowBg,
      labelBgStyle = props.labelBgStyle,
      labelBgPadding = props.labelBgPadding,
      labelBgBorderRadius = props.labelBgBorderRadius;

  var _useSmartEdge = useSmartEdge(),
      gridRatio = _useSmartEdge.gridRatio,
      nodePadding = _useSmartEdge.nodePadding,
      lineType = _useSmartEdge.lineType,
      lessCorners = _useSmartEdge.lessCorners;

  var roundCoordinatesTo = gridRatio; // We use the node's information to generate bounding boxes for them
  // and the graph

  var _getBoundingBoxes = getBoundingBoxes(storeNodes, nodePadding, roundCoordinatesTo),
      graph = _getBoundingBoxes.graph,
      nodes = _getBoundingBoxes.nodes;

  var source = {
    x: sourceX,
    y: sourceY,
    position: sourcePosition
  };
  var target = {
    x: targetX,
    y: targetY,
    position: targetPosition
  }; // With this information, we can create a 2D grid representation of
  // our graph, that tells us where in the graph there is a "free" space or not

  var _createGrid = createGrid(graph, nodes, source, target, gridRatio),
      grid = _createGrid.grid,
      start = _createGrid.start,
      end = _createGrid.end; // We then can use the grid representation to do pathfinding


  var _generatePath = generatePath(grid, start, end, lessCorners),
      fullPath = _generatePath.fullPath,
      smoothedPath = _generatePath.smoothedPath;
  /*
    Fallback to BezierEdge if no path was found.
    length = 0: no path was found
    length = 1: starting and ending points are the same
    length = 2: a single straight line from point A to point B
  */


  if (smoothedPath.length <= 2) {
    if (lineType === 'curve') {
      return React.createElement(BezierEdge, Object.assign({}, props));
    }

    return React.createElement(StraightEdge, Object.assign({}, props));
  } // Here we convert the grid path to a sequence of graph coordinates.


  var graphPath = smoothedPath.map(function (gridPoint) {
    var x = gridPoint[0],
        y = gridPoint[1];
    var graphPoint = gridToGraphPoint({
      x: x,
      y: y
    }, graph.xMin, graph.yMin, gridRatio);
    return [graphPoint.x, graphPoint.y];
  }); // Finally, we can use the graph path to draw the edge

  var svgPathString = lineType === 'curve' ? drawSmoothLinePath(source, target, graphPath) : drawStraightLinePath(source, target, graphPath); // The Label, if any, should be placed in the middle of the path

  var _fullPath$Math$floor = fullPath[Math.floor(fullPath.length / 2)],
      middleX = _fullPath$Math$floor[0],
      middleY = _fullPath$Math$floor[1];

  var _gridToGraphPoint = gridToGraphPoint({
    x: middleX,
    y: middleY
  }, graph.xMin, graph.yMin, gridRatio),
      labelX = _gridToGraphPoint.x,
      labelY = _gridToGraphPoint.y;

  var text = label ? React.createElement(EdgeText, {
    x: labelX,
    y: labelY,
    label: label,
    labelStyle: labelStyle,
    labelShowBg: labelShowBg,
    labelBgStyle: labelBgStyle,
    labelBgPadding: labelBgPadding,
    labelBgBorderRadius: labelBgBorderRadius
  }) : null;
  var markerEnd = getMarkerEnd(arrowHeadType, markerEndId);
  return React.createElement(React.Fragment, null, React.createElement("path", {
    style: style,
    className: "react-flow__edge-path",
    d: svgPathString,
    markerEnd: markerEnd
  }), text);
});
var DebouncedPathFindingEdge = /*#__PURE__*/memo(function (props) {
  var storeNodes = useStoreState(function (state) {
    return state.nodes;
  });

  var _useSmartEdge2 = useSmartEdge(),
      debounceTime = _useSmartEdge2.debounceTime;

  var _useState = useState(_extends({
    storeNodes: storeNodes
  }, props)),
      debouncedProps = _useState[0],
      setDebouncedProps = _useState[1];

  useDebounce(function () {
    setDebouncedProps(_extends({
      storeNodes: storeNodes
    }, props));
  }, debounceTime, [props, storeNodes]);
  return React.createElement(PathFindingEdge, Object.assign({}, debouncedProps));
});
var RegularPathFindingEdge = /*#__PURE__*/memo(function (props) {
  var storeNodes = useStoreState(function (state) {
    return state.nodes;
  });
  return React.createElement(PathFindingEdge, Object.assign({
    storeNodes: storeNodes
  }, props));
});
var SmartEdge = /*#__PURE__*/memo(function (props) {
  var context = useContext(SmartEdgeContext);

  if (!context) {
    return React.createElement(SmartEdgeProvider, null, React.createElement(DebouncedPathFindingEdge, Object.assign({}, props)), ";");
  }

  if (context.debounceTime === 0) {
    return React.createElement(RegularPathFindingEdge, Object.assign({}, props));
  }

  return React.createElement(DebouncedPathFindingEdge, Object.assign({}, props));
});
export default SmartEdge;
export { SmartEdge, SmartEdgeProvider };